import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, XCircle, Clock, Lock } from 'lucide-react';
import { buildCriteriaDetails } from './eligibility/eligibilityCriteria';

const EligibilityProgress = ({ eligibilityData }) => {
  if (!eligibilityData) return null;

  const { eligible, criteria, reasons, rate_limits, qualified_at } = eligibilityData;
  const criteriaDetails = buildCriteriaDetails(criteria);
  const totalCriteria = criteriaDetails.length;
  const metCriteria = criteriaDetails.filter(c => c.met).length;
  const overallProgress = (metCriteria / totalCriteria) * 100;

  return (
    <div className="space-y-6">
      {/* Overall Status Card */}
      <Card className={`border-2 ${eligible ? 'border-emerald-500 bg-emerald-50' : 'border-orange-500 bg-orange-50'}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {eligible ? (
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <Lock className="w-7 h-7 text-white" />
                </div>
              )}
              <div>
                <CardTitle className="text-2xl">
                  {eligible ? 'You\'re Qualified!' : 'Not Yet Qualified'}
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  {eligible 
                    ? 'You can now send private introductions to MyInvestFolio'
                    : `${metCriteria} of ${totalCriteria} criteria met`
                  }
                </CardDescription>
              </div>
            </div>
            {qualified_at && (
              <div className="text-right">
                <p className="text-sm text-gray-600">Qualified on</p>
                <p className="text-sm font-semibold text-gray-800">
                  {new Date(qualified_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Overall Progress</span>
              <span className="font-bold">{Math.round(overallProgress)}%</span>
            </div>
            <Progress value={overallProgress} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Rate Limits (if eligible) */}
      {eligible && rate_limits && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span>Introduction Limits</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">Pending Introductions</span>
              <span className="font-semibold text-gray-900">
                {rate_limits.pending_introductions} / {rate_limits.max_pending}
              </span>
            </div>
            <Progress 
              value={(rate_limits.pending_introductions / rate_limits.max_pending) * 100} 
              className="h-2"
            />
            <div className="flex justify-between items-center pt-2">
              <span className="text-sm text-gray-700">This Week</span>
              <span className="font-semibold text-gray-900">
                {rate_limits.recent_introductions_7_days} / {rate_limits.max_per_week}
              </span>
            </div>
            <Progress 
              value={(rate_limits.recent_introductions_7_days / rate_limits.max_per_week) * 100} 
              className="h-2"
            />
          </CardContent>
        </Card>
      )}

      {/* Detailed Criteria */}
      <Card>
        <CardHeader>
          <CardTitle>Eligibility Requirements</CardTitle>
          <CardDescription>
            Meet all criteria below to qualify for private introductions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {criteriaDetails.map((criterion) => {
              const Icon = criterion.icon;
              return (
                <div key={criterion.key} className="space-y-2">
                  <div className="flex items-start space-x-3">
                    <div className={`mt-1 ${criterion.met ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {criterion.met ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <XCircle className="w-6 h-6" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Icon className={`w-4 h-4 ${criterion.met ? 'text-emerald-600' : 'text-gray-500'}`} />
                        <h4 className={`font-semibold ${criterion.met ? 'text-emerald-900' : 'text-gray-700'}`}>
                          {criterion.title}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{criterion.description}</p>
                      {criterion.progress !== undefined && !criterion.met && (
                        <div className="mt-2">
                          <Progress value={criterion.progress} className="h-2" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Reasons (if not eligible) */}
      {!eligible && reasons && reasons.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-lg text-orange-900">What's Missing?</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {reasons.map((reason, index) => (
                <li key={`tip-${index}`} className="flex items-start space-x-2 text-sm text-orange-800">
                  <span className="text-orange-600 mt-0.5">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EligibilityProgress;
